import { defineComponent as _defineComponent } from 'vue'
export interface DateTimePickerProps {
  value?: boolean
  date?: string
}

import { offset, flip, shift } from '@floating-ui/vue'
import { computed, defineComponent, nextTick, ref, watch } from 'vue'

import { unwrapElement, useDismiss, usePopover } from '@/composables/popover'
import LButton from '@/components/core/Button.vue'
import { ensureTimeWithSeconds, extractTime } from '@/utils/date-utils'

const __default__ = defineComponent({
  inheritAttrs: false,
})

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'DateTimePicker',
  props: {
    value: { type: Boolean, default: undefined },
    date: { default: undefined }
  },
  emits: ["update:date", "input"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:date', value?: string): void
  (e: 'input', value: boolean): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: boolean, date: string };





// Popover Functionality -------------------------------------------------------

const internalValue = ref(false)
const isOpen = computed({
  get: () => props.value ?? internalValue.value,
  set: (value: boolean) => {
    internalValue.value = value
    emit('input', value)
  },
})

watch(isOpen, (newIsOpen) => {
  if (!newIsOpen) {
    // focus reference element when popover is closed
    nextTick(() => {
      const referenceElement = unwrapElement(referenceRef.value)
      if (!(referenceElement instanceof HTMLElement)) {
        return
      }
      // focus the trigger element directly or the reference element div
      const elementToFocus = referenceElement.firstChild ?? referenceElement
      if (elementToFocus instanceof HTMLElement) {
        elementToFocus.focus()
      }
    })
  } else {
    // focus floating element when popover is opened
    nextTick(() => {
      if (floatingRef.value instanceof HTMLElement) {
        floatingRef.value.focus()
      }
    })
  }
})

const { referenceRef, floatingRef, floatingStyles } = usePopover({
  placement: 'bottom-start',
  isOpen,
  middleware: [
    offset(4),
    flip({ padding: 16, fallbackPlacements: ['top-start'] }),
    shift({ padding: 16, crossAxis: true }),
  ],
})
useDismiss({ referenceRef, floatingRef, isOpen })

// Date ------------------------------------------------------------------------

const date = ref<string>(props.date ? props.date.split('T')[0] : '')

// Time ------------------------------------------------------------------------

const time = ref(extractTime(props.date))

// Handlers --------------------------------------------------------------------

watch(
  () => props.date,
  (newDate) => {
    if (!newDate) {
      date.value = ''
      time.value = ''
      return
    }

    date.value = newDate.split('T')[0]
    time.value = extractTime(newDate)
  },
)

function saveDate() {
  if (!date.value) {
    clearDate()
    return
  }

  const dateTimeString = time.value
    ? `${date.value}T${ensureTimeWithSeconds(time.value)}`
    : date.value

  emit('update:date', dateTimeString)
  isOpen.value = false
}

function clearDate() {
  emit('update:date', undefined)
  isOpen.value = false
}

const isChromiumBrowser = computed(() => {
  return 'chrome' in window
})

return { __sfc: true,props, emit, internalValue, isOpen, referenceRef, floatingRef, floatingStyles, date, time, saveDate, clearDate, isChromiumBrowser, LButton }
}

})